/**
 * Button Loading Manager
 * Gerencia o estado de loading de botões de adicionar ao carrinho
 * de forma reutilizável para diferentes componentes
 */
(function () {
  'use strict';

  /** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
  const loadingTimeouts = new WeakMap();

  /**
   * Reseta o estado do botão
   * @param {HTMLElement | null | undefined} button
   */
  function resetButtonState(button) {
    if (!button) return;
    button.setAttribute('aria-busy', 'false');
    if ('disabled' in button) {
      /** @type {HTMLButtonElement} */ (button).disabled = false;
    }
  }

  /**
   * @param {HTMLElement} button
   * @returns {boolean}
   */
  function isIconQuickAddButton(button) {
    return Boolean(
      button.closest('.quick-add--icon') ||
        button.classList.contains('cart-complementary-product__add-to-cart--icon-only')
    );
  }

  /**
   * Calcula o min-width do botão baseado no tamanho atual
   * para manter o tamanho durante o loading
   * @param {HTMLElement} button
   * @returns {number | null}
   */
  function calculateMinWidth(button) {
    void button.offsetWidth;

    const rect = button.getBoundingClientRect();

    // Ícone: lado do quadrado = menor dimensão (desktop hover expandido → altura)
    if (isIconQuickAddButton(button)) {
      return Math.min(rect.width, rect.height) || Math.max(rect.width, rect.height);
    }

    const computedStyle = window.getComputedStyle(button);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;
    const borderRight = parseFloat(computedStyle.borderRightWidth) || 0;

    return Math.max(rect.width, paddingLeft + paddingRight + borderLeft + borderRight + 50);
  }

  /**
   * Limpa dimensões inline aplicadas no loading
   * @param {HTMLElement} button
   */
  function clearLoadingDimensions(button) {
    button.style.minWidth = '';
    button.style.minHeight = '';
    button.style.width = '';
    button.style.height = '';
  }

  /**
   * Inicia o estado de loading no botão
   * @param {HTMLElement | null | undefined} button
   * @param {number} [duration=2000]
   */
  function startLoading(button, duration = 2000) {
    if (!button) return;

    const previousTimeout = loadingTimeouts.get(button);
    if (previousTimeout) clearTimeout(previousTimeout);

    const rect = button.getBoundingClientRect();
    const isIcon = isIconQuickAddButton(button);

    if (isIcon) {
      // Desktop hover: botão está expandido — alvo é o lado menor (quadrado do ícone)
      const side = Math.min(rect.width, rect.height) || Math.max(rect.width, rect.height);

      if (side > 0) {
        button.style.width = `${rect.width}px`;
        button.style.height = `${rect.height}px`;
        button.style.minWidth = `${rect.width}px`;
        button.style.minHeight = `${rect.height}px`;
        void button.offsetWidth;

        requestAnimationFrame(function () {
          button.style.width = `${side}px`;
          button.style.height = `${side}px`;
          button.style.minWidth = `${side}px`;
          button.style.minHeight = `${side}px`;
        });
      }
    } else {
      const minWidth = calculateMinWidth(button);

      if (minWidth && minWidth > 0) {
        button.style.minWidth = `${minWidth}px`;
      }

      if (rect.height > 0) {
        button.style.minHeight = `${rect.height}px`;
      }
    }

    void button.offsetWidth;

    button.setAttribute('aria-busy', 'true');
    if ('disabled' in button) {
      /** @type {HTMLButtonElement} */ (button).disabled = true;
    }

    const timeoutId = setTimeout(function () {
      resetButtonState(button);
      setTimeout(function () {
        clearLoadingDimensions(button);
      }, 50);
      loadingTimeouts.delete(button);
    }, duration);

    loadingTimeouts.set(button, timeoutId);
  }

  /**
   * Para o loading imediatamente
   * @param {HTMLElement | null | undefined} button
   */
  function stopLoading(button) {
    if (!button) return;

    const previousTimeout = loadingTimeouts.get(button);
    if (previousTimeout) {
      clearTimeout(previousTimeout);
      loadingTimeouts.delete(button);
    }

    resetButtonState(button);
    clearLoadingDimensions(button);
  }

  /**
   * @param {HTMLElement | null} target
   * @returns {HTMLButtonElement | null}
   */
  function findQuickAddButton(target) {
    if (!target || typeof target.closest !== 'function') return null;
    return (
      target.closest('button.quick-add__button') ||
      target.closest('button.cart-complementary-product__add-to-cart')
    );
  }

  /**
   * Delegação: funciona com cards renderizados/morphados depois do load
   */
  function bindQuickAddDelegation() {
    if (document.documentElement.dataset.quickAddLoadingBound === 'true') return;
    document.documentElement.dataset.quickAddLoadingBound = 'true';

    document.addEventListener(
      'click',
      function (event) {
        const button = findQuickAddButton(/** @type {HTMLElement} */ (event.target));
        if (!button || button.getAttribute('aria-busy') === 'true') return;

        const isComplementary = button.classList.contains('cart-complementary-product__add-to-cart');

        // Multi-variante (type=button): loading no quick-add.js handleClick
        // Complementary: arranca já no capture (sync) — rAF reabria o spinner depois do finally
        if (button.type !== 'submit') {
          if (isComplementary) {
            startLoading(button, 15000);
          }
          return;
        }

        // Submit: 1 frame para não bloquear o submit nativo
        requestAnimationFrame(function () {
          if (button.getAttribute('aria-busy') !== 'true') {
            startLoading(button, 2000);
          }
        });
      },
      true
    );

    document.addEventListener(
      'submit',
      function (event) {
        const form = /** @type {HTMLFormElement} */ (event.target);
        if (!(form instanceof HTMLFormElement)) return;

        const complementaryButton = form.querySelector('button.cart-complementary-product__add-to-cart');
        if (complementaryButton instanceof HTMLButtonElement) {
          if (complementaryButton.getAttribute('aria-busy') === 'true') return;
          requestAnimationFrame(function () {
            startLoading(complementaryButton, 2000);
          });
          return;
        }

        if (!form.closest('quick-add-component')) return;

        const button =
          form.querySelector('button.quick-add__button[type="submit"]') ||
          form.querySelector('button.quick-add__button');

        if (!(button instanceof HTMLButtonElement)) return;
        if (button.getAttribute('aria-busy') === 'true') return;

        requestAnimationFrame(function () {
          startLoading(button, 2000);
        });
      },
      true
    );

    document.addEventListener('cart:update', function (event) {
      const source = /** @type {Event & { target?: Element | null }} */ (event).target;
      const root = source instanceof Element ? source.closest('quick-add-component') || document : document;
      const scope = root instanceof Element ? root : document;
      scope.querySelectorAll('button.quick-add__button[aria-busy="true"]').forEach(function (btn) {
        stopLoading(/** @type {HTMLElement} */ (btn));
      });
      document
        .querySelectorAll('button.cart-complementary-product__add-to-cart[aria-busy="true"]')
        .forEach(function (btn) {
          stopLoading(/** @type {HTMLElement} */ (btn));
        });
    });

    document.addEventListener('cart:error', function () {
      document.querySelectorAll('button.quick-add__button[aria-busy="true"]').forEach(function (btn) {
        stopLoading(/** @type {HTMLElement} */ (btn));
      });
      document
        .querySelectorAll('button.cart-complementary-product__add-to-cart[aria-busy="true"]')
        .forEach(function (btn) {
          stopLoading(/** @type {HTMLElement} */ (btn));
        });
    });
  }

  /**
   * Inicializa o loading para botões dentro de um seletor específico (legado sticky/complementary)
   * @param {string} selector
   * @param {number} [duration=2000]
   */
  function initButtonLoading(selector, duration = 2000) {
    const buttons = document.querySelectorAll(selector);

    buttons.forEach(function (button) {
      if (!(button instanceof HTMLElement)) return;
      if (/** @type {any} */ (button)._loadingInitialized) return;
      /** @type {any} */ (button)._loadingInitialized = true;

      // Quick-add usa delegação global — evitar listeners duplicados
      if (button.classList.contains('quick-add__button')) return;

      const formComponent = button.closest('product-form-component');
      if (formComponent) {
        formComponent.addEventListener('submit', function () {
          requestAnimationFrame(function () {
            startLoading(button, duration);
          });
        });
      }

      if (button instanceof HTMLButtonElement && button.type === 'submit') {
        const form = button.closest('form');
        if (form) {
          form.addEventListener('submit', function () {
            requestAnimationFrame(function () {
              startLoading(button, duration);
            });
          });
        }
      }

      function handleCartUpdate() {
        stopLoading(button);
      }

      document.addEventListener('cart:update', handleCartUpdate);
      document.addEventListener('cart:error', handleCartUpdate);
    });
  }

  window.ButtonLoading = {
    init: initButtonLoading,
    start: startLoading,
    stop: stopLoading,
    reset: resetButtonState,
  };

  // Delegação imediata (não esperar setTimeout) para o primeiro clique já ter loading
  bindQuickAddDelegation();

  function initAllButtonLoading() {
    bindQuickAddDelegation();
    initButtonLoading('.cart-complementary-product__add-to-cart', 2000);
    initButtonLoading('.product-sticky-cart__add-to-cart', 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(initAllButtonLoading, 100);
    });
  } else {
    setTimeout(initAllButtonLoading, 100);
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      setTimeout(initAllButtonLoading, 200);
    });
  }
})();
